// Pre-fills the Shopify checkout email with the quiz email, so the order lands on the same
// Shopify customer the quiz created. The buyer can still change it at checkout.
//
// The email is set on the cart itself with the Storefront API (tokenless access covers
// cartBuyerIdentityUpdate), and checkout pre-fills from the cart's buyer identity. The
// checkout[email] URL parameter is ignored by the current Shopify checkout, so it is not used.
//
// Because the email lives on the cart, it does not matter which button opens checkout. The
// Slide Cart app drawer renders its own Checkout button we cannot hook, so the cart is kept
// in sync instead: on page load and after every cart write from any script or app. The
// results-page button also sets it just before redirecting, as a backstop.
//
// Logged-in customers are left alone: Shopify already uses their account email, and the
// cached quiz identity may belong to a previous customer on a shared browser. Any failure
// leaves checkout exactly as it was.
//
// Kept out of custom.js and wrapped so it declares no globals of its own: a top-level
// const/let clash with any inline script (custom.js's `observer` clashes on the results
// page) stops the whole file from running.
(function () {
  var STOREFRONT_API = '/api/2026-07/graphql.json';
  var TIMEOUT_MS = 3000;
  var SYNC_DEBOUNCE_MS = 150;
  var SYNCED_KEY = 'alyve:checkout-email-synced';
  // On a shared browser the cached quiz email can belong to someone long gone, so it is only
  // trusted for 30 days after this browser first stored it. Stamped here rather than in the
  // quiz, so existing visitors start their clock on the first page load after release.
  var SEEN_KEY = 'alyve:checkout-email-seen';
  var MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var MUTATION =
    'mutation AlyveCheckoutEmail($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {' +
    ' cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {' +
    ' cart { checkoutUrl } userErrors { field message } } }';
  var redirecting = false;
  var syncTimer = null;

  // Back from checkout can restore this page from the bfcache with the flag still set.
  window.addEventListener('pageshow', function () {
    redirecting = false;
  });

  function guestQuizEmail() {
    if (window.customer_email) return '';
    var email = '';
    try {
      var data = JSON.parse(localStorage.getItem('alyve_customer_data') || '{}');
      email = (data && data.email ? String(data.email) : '').trim();
    } catch (e) {}
    if (!email && window.email) email = String(window.email).trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return '';
    return isFresh(email) ? email : '';
  }

  function isFresh(email) {
    try {
      var seen = JSON.parse(localStorage.getItem(SEEN_KEY) || 'null');
      if (seen && seen.email === email && typeof seen.at === 'number') {
        return Date.now() - seen.at < MAX_AGE_MS;
      }
      localStorage.setItem(SEEN_KEY, JSON.stringify({ email: email, at: Date.now() }));
    } catch (e) {}
    return true;
  }

  function readSynced() {
    try {
      return sessionStorage.getItem(SYNCED_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeSynced(value) {
    try {
      sessionStorage.setItem(SYNCED_KEY, value);
    } catch (e) {}
  }

  // Sets the email on the current cart and resolves with its checkoutUrl, or null when
  // nothing was set. With onlyIfNeeded, skips empty carts and a cart+email pair already set
  // in this tab, so ordinary page loads cost one /cart.js read at most.
  function setCartEmail(email, onlyIfNeeded) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, TIMEOUT_MS);
    var signal = controller ? controller.signal : undefined;
    var syncedAs = null;

    return fetch('/cart.js', { signal: signal })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (!cart || !cart.token) return null;
        syncedAs = cart.token + '|' + email;
        if (onlyIfNeeded && (!cart.item_count || readSynced() === syncedAs)) return null;
        return fetch(STOREFRONT_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: signal,
          body: JSON.stringify({
            query: MUTATION,
            variables: {
              cartId: 'gid://shopify/Cart/' + cart.token,
              buyerIdentity: { email: email },
            },
          }),
        })
          .then(function (res) {
            return res.json();
          })
          .then(function (json) {
            var result = json && json.data && json.data.cartBuyerIdentityUpdate;
            if (!result || (result.userErrors && result.userErrors.length)) {
              console.warn('[alyve-checkout] buyer identity not set', result && result.userErrors);
              return null;
            }
            writeSynced(syncedAs);
            return (result.cart && result.cart.checkoutUrl) || null;
          });
      })
      .catch(function (err) {
        console.warn('[alyve-checkout] cart email not set', err && err.message);
        return null;
      })
      .finally(function () {
        clearTimeout(timer);
      });
  }

  function syncSoon() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      var email = guestQuizEmail();
      if (email) setCartEmail(email, true);
    }, SYNC_DEBOUNCE_MS);
  }

  // Watch for cart writes from anything on the page (theme, Slide Cart, other apps) without
  // touching their code: every completed request shows up as a resource timing entry.
  if (typeof PerformanceObserver === 'function') {
    try {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          if (CART_WRITE.test(entries[i].name)) {
            syncSoon();
            return;
          }
        }
      }).observe({ type: 'resource', buffered: true });
    } catch (e) {}
  }

  syncSoon();

  window.alyveGoToCheckout = function () {
    if (redirecting) return Promise.resolve();
    redirecting = true;

    var email = guestQuizEmail();
    if (!email) {
      window.location.href = '/checkout';
      return Promise.resolve();
    }

    return setCartEmail(email, false).then(function (url) {
      window.location.href = url || '/checkout';
    });
  };
})();
